import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, createVNode as _createVNode, createBlock as _createBlock } from "vue"

const _hoisted_1 = { id: "modal-container-element" }
const _hoisted_2 = { key: 1 }
const _hoisted_3 = { class: "search-container" }
const _hoisted_4 = ["value"]
const _hoisted_5 = ["rows", "columns", "selectable", "total-items", "current-page", "loading"]

import { computed, onMounted, onUnmounted, ref, watchEffect, watch } from 'vue';
import { useStore } from 'vuex';
import { debounce } from 'lodash';
import Banner from '@components/Banner/Banner.vue';
import { EPINIO_TYPES } from '../../../../types';
import Masthead from '@shell/components/ResourceList/Masthead';
import {
  makeActionMenu,
  makeStateTag,
  makeAppRoutesCell,
  makeNameLinks,
} from '../../../../utils/table-formatters';
import AppModal from '../../../../components/application/AppModal.vue';
import AppDeleteModal from '../../../../components/application/AppDeleteModal.vue';
import BulkDeleteModal from '../../../../components/BulkDeleteModal.vue';
import ExportAppModal from '../../../../dialog/ExportAppModal.vue';
import { useApplications } from '../../../../queries/useApplicationQueries';
import { useBulkRemoveApplications } from '../../../../queries/useApplicationMutations';
import { App } from '../../../../models/application/ui-types';
import { ListResourceRequestParams, ResourceQueryOptions, ResourceTableRow } from '../../../../models/resource/ui-types';
import { useUser } from '../../../../queries/useUserQueries';
import { showAppShell } from '../../../../models/application/actions/shell';
import { showAppLog, showStagingLog } from '../../../../models/application/actions/logs';
import { restageApp, restartApp } from '../../../../models/application/actions/restage';
import ServiceInstanceModal from '../../../../components/service/ServiceInstanceModal.vue';
import ConfigurationModal from '../../../../components/configuration/ConfigurationModal.vue';
import { fetchConfiguration } from '../../../../queries/useConfigurationQueries';
import { fetchService } from '../../../../queries/useServiceQueries';


export default /*@__PURE__*/_defineComponent({
  __name: 'index',
  setup(__props) {

const store = useStore() as any;
const t = store.getters['i18n/t'];
const router = store.$router;

const appModal = ref<InstanceType<typeof AppModal> | null>(null);
const deleteModal = ref<InstanceType<typeof AppDeleteModal> | null>(null);
const bulkDeleteModal = ref<InstanceType<typeof BulkDeleteModal> | null>(null);
const exportAppModal = ref<InstanceType<typeof ExportAppModal> | null>(null);
const serviceModal = ref<InstanceType<typeof ServiceInstanceModal> | null>(null);
const configModal = ref<InstanceType<typeof ConfigurationModal> | null>(null);

const resource: string = EPINIO_TYPES.APP;
const schema = ref(store.getters['epinio/schemaFor'](resource));

const { data: user, isError: isErrorUser, error: userError } = useUser(store);

const requestParams = ref<ListResourceRequestParams>({
  page: 1,
  pageSize: 10,
  search: '',
  namespaces: []
});

const requestOptions = ref<ResourceQueryOptions>({
  enabled: true,
  polling: true,
  isTablePagination: true,
});

const searchQuery = ref<string>('');

watch(searchQuery, (newQuery) => {
  onSearch(newQuery);
});

const onSearch = debounce(async (query: string) => {
  requestParams.value.page = 1;
  requestParams.value.search = query;
}, 500);

const {data: applications, isLoading: isLoadingApplications, isError: isErrorApplications, error: applicationsError} = useApplications(store, requestParams, requestOptions);
const { mutateAsync: bulkRemove } = useBulkRemoveApplications(store);

const tableEl = ref<any>(null);
const selectedRows = ref<any[]>([]);

const canCreate = computed(() => {
  return user.value?.permissions?.app_create || user.value?.permissions?.app_write || user.value?.permissions?.app;
});
const canEdit = computed(() => {
  return user.value?.permissions?.app_update || user.value?.permissions?.app_write || user.value?.permissions?.app;
});
const canDelete = computed(() => {
  return user.value?.permissions?.app_delete || user.value?.permissions?.app_write || user.value?.permissions?.app;
});
const canExport = computed(() => {
  return user.value?.permissions?.app_export || user.value?.permissions?.app_write || user.value?.permissions?.app;
});
const canExec = computed(() => {
  return user.value?.permissions?.app_exec  || user.value?.permissions?.app;
});
const canLogs = computed(() => {
  return user.value?.permissions?.app_logs || user.value?.permissions?.app;
});
const canStage = computed(() => {
  return user.value?.permissions?.app_stage || user.value?.permissions?.app_write || user.value?.permissions?.app;
});
const canRestart = computed(() => {
  return user.value?.permissions?.app_restart || user.value?.permissions?.app_write || user.value?.permissions?.app;
});

const handleCreateClick = () => {
  appModal.value?.openCreate();
};

// Watch for changes to the active namespace cache and update the request params accordingly
watchEffect(() => {
  void store.state.activeNamespaceCacheKey;
  const activeNamespaces = store.state.activeNamespaceCache;

  if (activeNamespaces && Object.keys(activeNamespaces).length > 0) {
    requestParams.value.namespaces = Object.keys(activeNamespaces);
  } else {
    requestParams.value.namespaces = undefined;
  }
});

const openDeleteModal = (app: App) => {
  deleteModal.value?.openDelete(app);
};

const openEditModal = (app: App) => {
  appModal.value?.openEdit(app);
};

const displayRows = computed(() => {
  if (!applications.value) {
    return [];
  }
  
  const rows: ResourceTableRow<App>[] = (applications.value.items ?? []).map((a) => ({
    ...a,
    id: a.meta.name, // stable, unique per namespace
    availableActions: [{
      label: 'App Shell',
      action: () => showAppShell(store, a),
      enabled: canExec.value && a.status === 'running',
      visible: canExec.value,
    }, {
      label: 'App Logs',
      action: () => showAppLog(store, a),
      enabled: canLogs.value && (a.status === 'running' || a.status === 'error'),
      visible: canLogs.value,
    }, {
      label: 'Last Build Logs',
      action: () => showStagingLog(store, a),
      enabled: canLogs.value && !!a.stageId,
      visible: canLogs.value,
    }, {
      label: 'Export',
      action: () => exportAppModal.value?.openExport(a),
      enabled: canExport.value && a.status === 'running',
      visible: canExport.value,
    }, {
      label: 'Restage',
      action: () => restageApp(store, a),
      enabled: canStage.value && a.canRetryBuild,
      visible: canStage.value,
    }, {
      label: 'Restart',
      action: () => restartApp(store, a),
      enabled: canRestart.value && a.status === 'running',
      visible: canRestart.value,
    }, {
      label: 'Edit',
      action: () => openEditModal(a),
      enabled: canEdit.value,
      visible: canEdit.value,
    }, {
      label: 'Delete',
      action: () => openDeleteModal(a),
      enabled: canDelete.value,
      visible: canDelete.value,
      danger: true,
    }],
    canDelete: canDelete.value,
  }));
  return rows;
});

onMounted(async () => {
  window.addEventListener('resize', onResize);

  const query = store.$router.currentRoute._value.query;

  if (query.mode === 'openModal') {
    appModal.value?.openCreate();
  }
});

onUnmounted(() => {
  window.removeEventListener('resize', onResize);
});

const openServiceModal = async (namespace: string, service: string) => {
  if (!namespace || !service) {
    return;
  }
  const serviceInstance = await fetchService(store, namespace, service);
  serviceModal.value?.openView(serviceInstance);
};

const openConfigurationModal = async (namespace: string, configName: string) => {
  if (!namespace || !configName) {
    return;
  }
  const configuration = await fetchConfiguration(store, namespace, configName);
  configModal.value?.openView(configuration);
};

// Services without service_write/service permission on that row can't be
// individually deleted, so they're excluded from bulk selection too.
const isRowSelectable = (row: any) => row.canDelete;

const handleSelectionChange = (event: CustomEvent) => {
  selectedRows.value = event.detail.selectedRows;
};

const handleBulkDeleteClick = () => {
  bulkDeleteModal.value?.openDelete(selectedRows.value);
};

const handleBulkDelete = async (items: App[], deleteImage: boolean, deletePVC: boolean) => {
  await bulkRemove({items, settings: {deleteImage, deletePVC, unmounted: true}});
};

const handleBulkDeleted = () => {
  selectedRows.value = [];
  tableEl.value?.clearSelection();
};

// NOTE: must be a named function declared here, not an inline arrow function
// in the template, see feedback_vue_ref_unwrap_bug.md. Vue auto-unwraps
// top-level refs inside template expressions, so referencing `tableEl`
// directly in an inline template callback gives the already-unwrapped
// (initially null) value instead of the Ref object, and `tableEl.value = el`
// throws, silently aborting the rest of the block.
const setTableRef = (el: any) => {
  if (el) {
    tableEl.value = el;
    el.renderActions = makeActionMenu;
    el.rowSelectable = isRowSelectable;
  }
};

const handleNavigate = (event: CustomEvent) => {
  router.push(event.detail.url);
};

const windowWidth = ref(window.innerWidth);
const onResize = () => { windowWidth.value = window.innerWidth; };

const allColumns = [
  {
    field:     'stateDisplay',
    label:     'State',
    width:     '110px',
    formatter: (_value: string, row: App) => makeStateTag(row)
  },
  {
    field: 'nameDisplay',
    label: 'Name',
    width: '125px',
    formatter: (_value: any, row: App) => makeNameLinks(
      [row.meta.name],
      { cluster: store.getters['clusterId'], namespace: row.meta.namespace, resource: EPINIO_TYPES.APP },
      router
    )
  },
  {
    field: 'meta.namespace',
    label: 'Namespace',
    width: '125px',
  },
  { field: 'deployment.status', label: 'Status', width: '75px' },
  {
    field:     'route',
    label:     'Routes',
    width:     '180px',
    sortable:  false,
    formatter: (_value: any, row: App) => makeAppRoutesCell(row)
  },
  {
    field:     'boundConfigs',
    label:     'Bound Configs',
    width:     '180px',
    sortable:  false,
    formatter: (_value: any, row: App) => {
      const configs = row.configuration.configurations || [];
      const span = document.createElement('span');

      span.style.whiteSpace = 'normal';
      span.style.overflowWrap = 'anywhere';
      span.style.wordBreak = 'break-word';

      configs.forEach((config, index) => {
        const a = document.createElement('a');

        try {
          a.href = '#';
        } catch {
          a.href = '#';
        }

        a.addEventListener('click', (e) => {
          e.preventDefault();
          openConfigurationModal(row.meta.namespace, config);
        });

        a.textContent = config;
        span.appendChild(a);

        if (index < configs.length - 1) {
          span.appendChild(document.createTextNode(', '));
        }
      });

      return span;
    }
  },
  {
    field:     'boundServices',
    label:     'Bound Services',
    width:     '180px',
    sortable:  false,
    formatter: (_value: any, row: App) => {
      const services = row.configuration.services || [];
      const span = document.createElement('span');

      span.style.whiteSpace = 'normal';
      span.style.overflowWrap = 'anywhere';
      span.style.wordBreak = 'break-word';

      services.forEach((service, index) => {
        const a = document.createElement('a');

        try {
          a.href = '#';
        } catch {
          a.href = '#';
        }

        a.addEventListener('click', (e) => {
          e.preventDefault();
          openServiceModal(row.meta.namespace, service);
        });

        a.textContent = service;
        span.appendChild(a);

        if (index < services.length - 1) {
          span.appendChild(document.createTextNode(', '));
        }
      });

      return span;
    }
  },
  { field: 'deployment.username', label: 'Last Deployed By', width: '150px' },
  { field: 'meta.createdAt',      label: 'Age',              width: '50px', formatter: 'age' }
];

const columns = computed(() => {
  const w = windowWidth.value;
  const hide = new Set<string>();

  if (w < 1900) hide.add('deployment.username');
  if (w < 1700) hide.add('deployment.status');
  if (w < 1600) { hide.add('boundConfigs'); hide.add('boundServices'); }
  if (w < 1150) hide.add('meta.createdAt');
  if (w < 875)  hide.add('route');

  return allColumns.filter(col => !hide.has(col.field));
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_unref(Masthead), {
      schema: schema.value,
      resource: _unref(resource)
    }, {
      extraActions: _withCtx(() => [
        (selectedRows.value.length)
          ? (_openBlock(), _createElementBlock("trailhand-button", {
              key: 0,
              variant: "destructive",
              size: "large",
              onClick: handleBulkDeleteClick
            }, [
              _cache[2] || (_cache[2] = _createElementVNode("trailhand-icon", { name: "trash" }, null, -1)),
              _createTextVNode("\n          Delete (" + _toDisplayString(selectedRows.value.length) + ")\n        ", 1)
            ]))
          : _createCommentVNode("", true)
      ]),
      createButton: _withCtx(() => [
        (canCreate.value)
          ? (_openBlock(), _createElementBlock("trailhand-button", {
              key: 0,
              variant: "primary",
              size: "large",
              onClick: handleCreateClick
            }, _toDisplayString(_unref(t)('generic.create')), 1))
          : (_openBlock(), _createElementBlock("div", _hoisted_2))
      ]),
      _: 1,
      __: [3]
    }, 8, ["schema", "resource"]),
    _cache[4] || (_cache[4] = _createTextVNode()),
    (_unref(isErrorUser))
      ? (_openBlock(), _createBlock(Banner, {
          key: 0,
          color: "error",
          label: _unref(userError)?.message || _unref(t)('epinio.user.errors.fetch')
        }, null, 8, ["label"]))
      : _createCommentVNode("", true),
    _cache[5] || (_cache[5] = _createTextVNode()),
    (_unref(isErrorApplications))
      ? (_openBlock(), _createBlock(Banner, {
          key: 1,
          color: "error",
          label: _unref(applicationsError)?.message || _unref(t)('epinio.applications.errors.fetch')
        }, null, 8, ["label"]))
      : _createCommentVNode("", true),
    _cache[6] || (_cache[6] = _createTextVNode()),
    _createElementVNode("div", _hoisted_3, [
      _createElementVNode("trailhand-text-input", {
        value: searchQuery.value,
        placeholder: "Search...",
        onTextInputChange: _cache[0] || (_cache[0] = (e) => searchQuery.value = e.detail.value)
      }, null, 40, _hoisted_4)
    ]),
    _cache[7] || (_cache[7] = _createTextVNode()),
    _createElementVNode("trailhand-table", {
      ref: setTableRef,
      rows: displayRows.value,
      columns: columns.value,
      searchable: false,
      selectable: canDelete.value,
      "server-side": true,
      "total-items": _unref(applications)?.totalItems ?? 0,
      "current-page": requestParams.value.page,
      loading: _unref(isLoadingApplications),
      "key-field": "id",
      onNavigate: handleNavigate,
      onPageChange: _cache[1] || (_cache[1] = (e) => { requestParams.value.page = e.detail.page; }),
      onSelectionChange: handleSelectionChange
    }, null, 40, _hoisted_5),
    _cache[8] || (_cache[8] = _createTextVNode()),
    _createVNode(AppModal, {
      ref_key: "appModal",
      ref: appModal
    }, null, 512),
    _cache[9] || (_cache[9] = _createTextVNode()),
    _createVNode(AppDeleteModal, {
      ref_key: "deleteModal",
      ref: deleteModal
    }, null, 512),
    _cache[10] || (_cache[10] = _createTextVNode()),
    _createVNode(ExportAppModal, {
      ref_key: "exportAppModal",
      ref: exportAppModal
    }, null, 512),
    _cache[11] || (_cache[11] = _createTextVNode()),
    _createVNode(BulkDeleteModal, {
      ref_key: "bulkDeleteModal",
      ref: bulkDeleteModal,
      "resource-label": "application",
      "resource-type": _unref(resource),
      "is-deleting-apps": true,
      "bulk-remove": handleBulkDelete,
      onSettled: handleBulkDeleted
    }, null, 8, ["resource-type"]),
    _cache[12] || (_cache[12] = _createTextVNode()),
    _createVNode(ConfigurationModal, {
      ref_key: "configModal",
      ref: configModal
    }, null, 512),
    _cache[13] || (_cache[13] = _createTextVNode()),
    _createVNode(ServiceInstanceModal, {
      ref_key: "serviceModal",
      ref: serviceModal
    }, null, 512)
  ]))
}
}

})